import { formatTime } from "@/helpers/formatDate";
import { formatPrice } from "@/helpers/formatPrice";
import { usePayClientCredit } from "@/services/clients/mutation";
import { Client, ClientPurchase } from "@/types/client";
import { Button, DatePicker, Input, message, Modal, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { DollarSign, FileText } from "lucide-react";
import { useMemo, useState } from "react";

const { RangePicker } = DatePicker;

interface ModalPayClientCreditProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: (newCreditBalance?: number) => void;
  client: Client;
  purchases: ClientPurchase[];
}

export function ModalPayClientCredit({
  open,
  onCancel,
  onSuccess,
  client,
  purchases,
}: ModalPayClientCreditProps) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const payClientCredit = usePayClientCredit();

  // Filtrar solo compras a crédito no pagadas
  const pendingCreditPurchases = useMemo(() => {
    return purchases.filter((item) => {
      const isCredit = item.method === "credit" || !item.method;
      const isPending = item.status === "pending" || !item.status;
      if (!isCredit || !isPending) return false;

      if (dateRange && dateRange[0] && dateRange[1]) {
        const itemDate = dayjs(item.created_at || item.date);
        const start = dateRange[0].startOf("day");
        const end = dateRange[1].endOf("day");
        if (itemDate.isBefore(start) || itemDate.isAfter(end)) {
          return false;
        }
      }

      return true;
    });
  }, [purchases, dateRange]);

  // Total acumulado de las compras seleccionadas
  const selectedTotal = useMemo(() => {
    return pendingCreditPurchases
      .filter((item) => selectedRowKeys.includes(item.id))
      .reduce((sum, item) => sum + (item.total || 0), 0);
  }, [pendingCreditPurchases, selectedRowKeys]);

  const handleSelectAllFiltered = () => {
    setSelectedRowKeys(pendingCreditPurchases.map((p) => p.id));
  };

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
  };

  const handleSubmit = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Selecciona al menos una compra a crédito.");
      return;
    }

    if (!invoiceNumber.trim()) {
      message.warning("Ingresa un número de factura.");
      return;
    }

    payClientCredit.mutate(
      {
        customer_id: client.id,
        payload: {
          payment_ids: selectedRowKeys as string[],
          invoice_number: invoiceNumber.trim(),
        },
      },
      {
        onSuccess: (res: any) => {
          message.success(
            `Se han marcado ${selectedRowKeys.length} ventas como pagadas con la factura ${invoiceNumber.trim()}`,
          );
          setSelectedRowKeys([]);
          setInvoiceNumber("");
          setDateRange(null);
          onSuccess?.(res?.data?.credit_balance);
          onCancel();
        },
        onError: (err: any) => {
          message.error(
            err?.response?.data?.message || "Ocurrió un error al procesar el pago.",
          );
        },
      },
    );
  };

  const columns: ColumnsType<ClientPurchase> = [
    {
      title: "N° Ticket",
      dataIndex: "number_ticket",
      key: "number_ticket",
      render: (text) => (text ? text : <Tag color="red">Sin N°</Tag>),
    },
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => formatTime(text),
    },
    {
      title: "Monto",
      dataIndex: "total",
      key: "total",
      render: (val) => formatPrice(val ?? 0),
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 text-emerald-500 font-semibold text-base">
          <DollarSign className="w-5 h-5 text-emerald-500" />
          <span>Bajar Crédito - Marcar Compras como Pagadas</span>
        </div>
      }
      centered
      open={open}
      width={750}
      onCancel={() => {
        setSelectedRowKeys([]);
        setInvoiceNumber("");
        setDateRange(null);
        onCancel();
      }}
      footer={
        <div className="flex items-center justify-end gap-4 pt-2">
          <Button
            onClick={() => {
              setSelectedRowKeys([]);
              setInvoiceNumber("");
              setDateRange(null);
              onCancel();
            }}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={
              selectedRowKeys.length === 0 ||
              !invoiceNumber.trim() ||
              payClientCredit.isPending
            }
            loading={payClientCredit.isPending}
            onClick={handleSubmit}
          >
            Confirmar pago de {selectedRowKeys.length} compras ({formatPrice(selectedTotal)})
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        {/* Info cliente y resumen de crédito */}
        <div className="bg-muted/40 p-3 rounded-lg border border-border text-sm flex flex-wrap justify-between gap-2">
          <div>
            <span className="text-muted-foreground">Cliente: </span>
            <strong className="text-foreground">{client?.name}</strong>
          </div>
          <div>
            <span className="text-muted-foreground">Crédito Ocupado Actual: </span>
            <strong className="text-amber-500">
              {formatPrice(Number(client?.credit_balance) || 0)}
            </strong>
          </div>
        </div>

        {/* Filtro por rango de fecha */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-card p-3 rounded-lg border border-border">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              Filtrar por periodo:
            </span>
            <RangePicker
              placeholder={["Fecha inicio", "Fecha fin"]}
              value={dateRange}
              onChange={(dates) => setDateRange(dates as any)}
              size="middle"
              className="w-full md:w-auto"
            />
          </div>

          <div className="flex gap-2">
            <Button size="small" onClick={handleSelectAllFiltered}>
              Seleccionar todas
            </Button>
            <Button size="small" onClick={handleClearSelection}>
              Limpiar selección
            </Button>
          </div>
        </div>

        {/* Tabla de compras no pagadas */}
        <Table<ClientPurchase>
          rowKey="id"
          size="small"
          rowSelection={{
            selectedRowKeys,
            onChange: (newKeys) => setSelectedRowKeys(newKeys),
          }}
          columns={columns}
          dataSource={pendingCreditPurchases}
          pagination={false}
          scroll={{ y: 240 }}
          locale={{ emptyText: "No hay compras a crédito pendientes en este periodo" }}
        />

        {/* Resumen de selección e Input de Factura */}
        <div className="bg-muted/30 p-3 rounded-lg border border-border space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span>Compras seleccionadas: <strong>{selectedRowKeys.length}</strong></span>
            <span>Total a saldar: <strong className="text-emerald-400 text-base">{formatPrice(selectedTotal)}</strong></span>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" />
              <span>Número de Factura:</span>
            </label>
            <Input
              name="invoice_document_number_custom"
              autoComplete="off"
              aria-autocomplete="none"
              data-lpignore="true"
              data-form-type="other"
              placeholder="Ingresa el N° de Factura (ej: F-1002)"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
